"use client";

import * as React from "react";
import Image from "next/image";
import { ExternalLink } from "lucide-react";
import { Card, LiquidPill } from "components/ui/v2";
import { isRenderableImageSrc } from "components/ui/v2/avatar";
import type { IVendorBanner } from "types/Vendor.types";

export interface VendorBannerSlideV2Props {
  banner: IVendorBanner;
  /** Compact height for config preview; roomy for portal home. */
  density?: "preview" | "portal";
  /** Show placeholder copy when title/description empty (editor). */
  showPlaceholders?: boolean;
  titlePlaceholder?: string;
  descriptionPlaceholder?: string;
  onCtaClick?: () => void;
}

/**
 * VendorBannerSlideV2 — solid content banner (§5.4 / §6.6 hero).
 * Media + overlay + typography solid; CTA = LiquidPill (control).
 */
export function VendorBannerSlideV2({
  banner,
  density = "portal",
  showPlaceholders = false,
  titlePlaceholder = "Título",
  descriptionPlaceholder = "Descripción",
  onCtaClick,
}: VendorBannerSlideV2Props) {
  const heightClass =
    density === "preview" ? "h-48 sm:h-56" : "h-44 sm:h-52 md:h-56";

  const title = banner.title?.trim();
  const description = banner.description?.trim();
  const btnText = banner.btnText?.trim();
  const cta = banner.cta?.trim();
  const hasCta =
    Boolean(btnText) &&
    Boolean(cta) &&
    cta !== "https://undefined";

  const showTitle = title || (showPlaceholders ? titlePlaceholder : "");
  const showDescription =
    description || (showPlaceholders ? descriptionPlaceholder : "");

  const handleCta = () => {
    if (onCtaClick) {
      onCtaClick();
      return;
    }
    if (hasCta && cta) {
      window.open(cta, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Card size="full" padding="none" shadow="subtle" fullHeight>
      <article
        className={`relative w-full overflow-hidden ${heightClass}`}
      >
        {isRenderableImageSrc(banner.bannerUrl) ? (
          <Image
            fill
            src={banner.bannerUrl}
            alt={title || "banner"}
            sizes="(max-width: 768px) 100vw, 960px"
            className="object-cover"
            priority={density === "portal"}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-sky-50 to-fuchsia-50 dark:from-indigo-950/80 dark:via-zinc-900 dark:to-fuchsia-950/50" />
        )}

        {/* Card §6.6 overlay — legibility without washing the media */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-2 p-3 sm:gap-2.5 sm:p-4 md:p-5">
          {showTitle ? (
            <h3 className="max-w-2xl text-base font-semibold leading-snug text-white line-clamp-2 sm:text-lg">
              {showTitle}
            </h3>
          ) : null}
          {showDescription ? (
            <p className="max-w-xl text-sm leading-6 text-white/90 line-clamp-2">
              {showDescription}
            </p>
          ) : null}
          {btnText ? (
            <LiquidPill
              as="button"
              type="button"
              variant="indigo"
              appearance="tinted"
              shadow="neutral"
              size="sm"
              icon={<ExternalLink className="h-3.5 w-3.5" />}
              text={btnText}
              aria-label={btnText}
              onClick={hasCta || onCtaClick ? handleCta : undefined}
              style={
                banner.btnColor
                  ? {
                      backgroundColor: banner.btnColor,
                      color: "#fff",
                    }
                  : undefined
              }
            />
          ) : null}
        </div>
      </article>
    </Card>
  );
}
